import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { PlusCircle, Pencil, Trash2, X } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { DocumentType } from "@/types";
import { SettingsSection } from "@/components/settings/SettingsSection";

type FormMode = "closed" | "add" | "edit";

export function DocumentTypeManager() {
  const [formMode, setFormMode] = useState<FormMode>("closed");
  const [selectedType, setSelectedType] = useState<DocumentType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentType | null>(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const { toast } = useToast();

  const { data: documentTypes = [], isLoading } = useQuery({
    queryKey: ["/api/document-types"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/document-types");
        const data = await res.json();
        return data || [];
      } catch (error) {
        console.error("Error fetching document types:", error);
        return [];
      }
    },
  });

  const addMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const res = await apiRequest("POST", "/api/document-types", data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-types"] });
      closeForm();
      toast({
        title: "Type added",
        description: "Document type added successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't add type",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async (data: {
      id: number;
      name: string;
      description: string;
    }) => {
      const res = await apiRequest("PUT", `/api/document-types/${data.id}`, {
        name: data.name,
        description: data.description,
      });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-types"] });
      closeForm();
      toast({
        title: "Type updated",
        description: "Document type updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't update type",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/document-types/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/document-types"] });
      setDeleteTarget(null);
      toast({
        title: "Type deleted",
        description: "Document type deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Couldn't delete type",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const closeForm = () => {
    setFormMode("closed");
    setSelectedType(null);
    setFormData({ name: "", description: "" });
  };

  const openAdd = () => {
    setSelectedType(null);
    setFormData({ name: "", description: "" });
    setFormMode("add");
  };

  const openEdit = (type: DocumentType) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      description: type.description || "",
    });
    setFormMode("edit");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formMode === "add") {
      addMutation.mutate(formData);
      return;
    }
    if (formMode === "edit" && selectedType) {
      editMutation.mutate({ id: selectedType.id, ...formData });
    }
  };

  const createDefaultTypes = async () => {
    try {
      await apiRequest("POST", "/api/document-types", {
        name: "Sermon",
        description: "A sermon or teaching document with special metadata fields",
      });
      await apiRequest("POST", "/api/document-types", {
        name: "Note",
        description: "A general purpose note or document",
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/document-types"] });
      toast({
        title: "Defaults created",
        description: "Sermon and Note types are ready to use.",
      });
    } catch {
      toast({
        title: "Couldn't create defaults",
        description: "Could not create default document types.",
        variant: "destructive",
      });
    }
  };

  const saving = addMutation.isPending || editMutation.isPending;

  return (
    <div className="space-y-4">
      <SettingsSection
        title="Document types"
        description="Available when you create a new document."
        action={
          documentTypes.length > 0 && formMode === "closed" ? (
            <Button type="button" size="sm" onClick={openAdd}>
              <PlusCircle className="mr-1.5 h-4 w-4" />
              Add type
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading document types…
          </div>
        ) : documentTypes.length === 0 && formMode === "closed" ? (
          <div className="space-y-4 rounded-xl border border-dashed border-border px-6 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              No document types yet. You need at least one to create documents.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button type="button" onClick={() => void createDefaultTypes()}>
                Create defaults
              </Button>
              <Button type="button" variant="outline" onClick={openAdd}>
                Add custom type
              </Button>
            </div>
          </div>
        ) : (
          <ul className="overflow-hidden rounded-xl border border-border divide-y divide-border">
            {documentTypes.map((type: DocumentType) => (
              <li
                key={type.id}
                className="flex items-start justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{type.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {type.description || "No description"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => openEdit(type)}
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteTarget(type)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>

      {formMode !== "closed" && (
        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-border p-4"
        >
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-medium">
              {formMode === "add" ? "Add document type" : "Edit document type"}
            </h4>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={closeForm}
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-type-name">Name</Label>
            <Input
              id="doc-type-name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Sermon, Note, Journal"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-type-description">Description</Label>
            <Textarea
              id="doc-type-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional short description"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeForm}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : formMode === "add"
                  ? "Add type"
                  : "Save changes"}
            </Button>
          </div>
        </form>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document type?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `“${deleteTarget.name}” will be removed. Existing documents keep their content, but you won’t be able to create new ones of this type.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
